/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { FC, useState, useEffect, CSSProperties, useCallback } from 'react';
import { getFirebaseToken } from '../../../../infra/auth/firebase-auth';
import appConfig from '../../../config/app';
import { postMessageToIframe } from '../../../utils/post-message';
import { createQueryParams } from '../../../utils/url-helpers';
import Loader from '../../molecules/loader';
import { MiniIframeEvent } from '../../../constants/mini-iframe-events';
import { getUserChannelData } from '../../../../modules/login/v1/utility';
import { WabaIntegration } from '../../../typings/waba';
import { useAtom, useSetAtom } from 'jotai';
import { userProfileAtom } from '../../../states/user';
import { aiBotUpgradeModalOpenAtom, facebookCapiUpgradeModalOpenAtom } from '../../../../modules/ai-bot/state';
import { TextOrMediaItemMediaItemType } from '../../../typings/quick-reply';
import { useMediaViewerV2 } from '../../../../modules/home/v1/hooks/use-media-viewer-v2';
import { qRMediaTypeToMessageType } from '../../../../modules/home/v1/components/chat-input/quick-reply/utils';

interface SettingsIframeProps {
  id: string;
  name?: string;
  route: string;
  queryParams: Array<{ key: string; value: string | undefined }>;
  title: string;
  sandbox?: string;
  iframeStyle?: CSSProperties;
  iFrameHostUrl?: string;
  onMessageReceived?: (messageEvent: MessageEvent) => void;
  onLoadIframe?: () => void;
  customUrl?: string;
}

interface MiniMessage {
  type: MiniIframeEvent;
  data?: {
    waba: WabaIntegration;
    mediaItems?: TextOrMediaItemMediaItemType[];
    index?: number;
  };
}

const SettingsIframe: FC<SettingsIframeProps> = ({
  id,
  name,
  route,
  queryParams = [],
  title,
  sandbox,
  // sandbox = [
  //   'allow-forms',
  //   'allow-modals',
  //   'allow-orientation-lock',
  //   'allow-pointer-lock',
  //   'allow-scripts',
  //   'allow-popups-to-escape-sandbox',
  //   'allow-same-origin',
  //   'allow-popups',
  //   'allow-presentation',
  //   'allow-top-navigation',
  //   'allow-downloads'
  // ].join(', '),
  iframeStyle,
  iFrameHostUrl,
  onMessageReceived,
  onLoadIframe,
  customUrl,
}) => {
  const [iframeLoaded, toggleIframeLoaded] = useState(false);
  const channelData = getUserChannelData();
  const [userProfile, setUserProfile] = useAtom(userProfileAtom);
  const setAiBotUpgradeModalOpen = useSetAtom(aiBotUpgradeModalOpenAtom);
  const setFacebookCapiUpgradeModalOpen = useSetAtom(facebookCapiUpgradeModalOpenAtom);
  const mediaViewerHandler = useMediaViewerV2();

  const getToken = useCallback(async () => {
    try {
      const token = await getFirebaseToken();
      return token;
    } catch (error) {
      // Do nothing
    }
  }, []);

  const sendToken = useCallback(async () => {
    const token = await getFirebaseToken();
    try {
      postMessageToIframe(
        id,
        JSON.stringify({
          data: { token },
          type: 'LOGIN',
        })
      );
      if (channelData?.name) {
        postMessageToIframe(
          id,
          JSON.stringify({
            type: 'CHANGE_CHANNEL_PARTNER',
            data: channelData,
          })
        );
      }
    } catch (error) {
      // Do nothing
    }
  }, [id]);

  const sendOrigin = useCallback(async () => {
    try {
      postMessageToIframe(
        id,
        JSON.stringify({
          data: { origin: window.location.origin },
          type: 'READ_ORIGIN',
        })
      );
    } catch (error) {
      // Do nothing
    }
  }, []);

  useEffect(() => {
    getToken();

    // refresh after 65 mins (5 mins buffer, as token gets refreshed every 60 mins)
    const interval = setInterval(() => {
      getToken();
    }, 65 * 60 * 1000);

    return () => clearInterval(interval);
  }, [getToken]);

  const openQuickReplyMediaViewer = (mediaItems: TextOrMediaItemMediaItemType[], index: number) => {
    mediaViewerHandler({
      message: {
        filesUrl: mediaItems.map((item) => item.fileUrl),
        activeIndex: index,
        messageTypes: mediaItems.map((item) => qRMediaTypeToMessageType(item.messageType)),
      },
    });
  };

  const sendPrimaryWaba = useCallback(async (waba: WabaIntegration) => {
    if (!userProfile?.primaryIntegration?.id || userProfile?.primaryIntegration?.id !== waba.id) {
      setUserProfile((prev) =>
        prev
          ? {
              ...prev,
              primaryIntegration: waba,
            }
          : undefined
      );
    }
  }, []);

  const miniMsgHandler = async (event: MessageEvent<MiniMessage>) => {
    if (!event || !event.data) {
      return;
    }
    switch (event.data.type) {
      case MiniIframeEvent.ASK_TOKEN: {
        sendToken();
        return;
      }
      case MiniIframeEvent.ASK_ORIGIN: {
        sendOrigin();
        return;
      }
      case MiniIframeEvent.OPEN_QUICK_REPLY_MEDIA_VIEWER: {
        if (event?.data?.data) {
          openQuickReplyMediaViewer(event?.data?.data?.mediaItems ?? [], event?.data?.data?.index ?? 0);
        }
        return;
      }
      case MiniIframeEvent.OPEN_AI_BOT_UPGRADE_MODAL: {
        setAiBotUpgradeModalOpen(true);
        return;
      }
      case MiniIframeEvent.OPEN_FACEBOOK_CAPI_UPGRADE_MODAL: {
        setFacebookCapiUpgradeModalOpen(true);
        return;
      }
      case MiniIframeEvent.UPDATE_PRIMARY_WABA: {
        if (event?.data?.data) {
          window.localStorage.removeItem('selectedWaba');
          sendPrimaryWaba(event.data.data.waba);
        }
        return;
      }
      default: {
        if (typeof onMessageReceived === 'function') {
          onMessageReceived(event);
        }
        return;
      }
    }
  };

  useEffect(() => {
    window.addEventListener('message', miniMsgHandler);
    const interval = setInterval(() => {
      sendToken();
    }, 65 * 60 * 1000);
    return () => {
      window.removeEventListener('message', miniMsgHandler);
      clearInterval(interval);
    };
  }, [sendToken, id, onMessageReceived]);

  const getQueryParams = () => {
    return createQueryParams([
      ...(queryParams || []),
      { key: 'source', value: 'desktop' },
      {
        key: 'parent-origin',
        value: encodeURIComponent(window.location.origin),
      },
    ]);
  };

  const getIframeAllowedFeatures = (): string[] => {
    const defaultIframeFeatures = [
      'clipboard-write',
      'clipboard-read',
      'microphone',
      'camera',
      'geolocation',
      'midi',
      'encrypted-media',
      'payment',
      'usb',
      'accelerometer',
      'gyroscope',
      'magnetometer',
      'autoplay',
      'display-capture',
      'fullscreen',
    ];
    try {
      const iframeFeatures = document.featurePolicy.allowedFeatures();
      return iframeFeatures.filter((feature) => defaultIframeFeatures.includes(feature));
    } catch (error) {
      return defaultIframeFeatures;
    }
  };

  const renderIframe = () => {
    return (
      <iframe
        id={id}
        css={css`
          width: 100%;
          height: 100%;
          border: none;
          outline: none;
          box-shadow: none;
        `}
        allow={getIframeAllowedFeatures().join('; ')}
        sandbox={sandbox}
        name={name ?? Date.now().toString()}
        src={customUrl ? customUrl : `${iFrameHostUrl ?? appConfig.iframeHost}${route}${getQueryParams()}`}
        title={title}
        style={{ display: iframeLoaded ? 'flex' : 'none', ...iframeStyle }}
        onLoad={() => {
          toggleIframeLoaded(true);
          if (typeof onLoadIframe === 'function') {
            onLoadIframe();
          }
        }}
      />
    );
  };

  const renderLoader = () => {
    return (
      <div
        css={css`
          display: flex;
          justify-content: center;
          align-items: center;
          flex-direction: column;
          color: white;
          width: 100%;
        `}
      >
        <Loader size="3rem" />
      </div>
    );
  };

  return (
    <div
      className={'settings-iframe-container'}
      css={css`
        display: flex;
        flex: 1;
        height: 100%;
        width: 100%;
        border: none;
      `}
    >
      {renderIframe()}
      {!iframeLoaded && renderLoader()}
    </div>
  );
};

export default SettingsIframe;
